import { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { SimWidget } from '../HomeFeatures';
import { playQuietly, useInView } from '../hooks';
import { DESK_FEATURES, DESK_TONES } from './desktopData';
import { BrowserFrame, Clip, CountStat } from './parts';
import PnlLedger, { PNL_FOOTNOTE } from './PnlLedger';
import './DesktopFeatures.css';

const IDLE_AFTER_CLICK_MS = 9000;

function Showcase() {
  const videoRefs = useRef([]);
  const fillRefs = useRef([]);
  const lastClick = useRef(0);
  const [active, setActive] = useState(0);
  const [wrapRef, visible] = useInView({ threshold: 0.35, once: false });
  const [seen, setSeen] = useState(() => new Set());

  useEffect(() => {
    if (visible) setSeen((prev) => (prev.has(active) ? prev : new Set(prev).add(active)));
    videoRefs.current.forEach((video, index) => {
      if (!video) return;
      if (index === active && visible) {
        playQuietly(video);
      } else {
        video.pause();
        // Seeking a clip that has only loaded metadata makes Chrome fail it with a decode error.
        if (index !== active && video.currentTime > 0) video.currentTime = 0;
      }
    });
  }, [active, visible]);

  useEffect(() => {
    if (typeof requestAnimationFrame === 'undefined') return undefined;
    let frame = 0;
    const tick = () => {
      fillRefs.current.forEach((fill, index) => {
        if (!fill) return;
        const video = videoRefs.current[index];
        const amount = index === active && video && video.duration ? video.currentTime / video.duration : 0;
        fill.style.transform = `scaleX(${amount})`;
      });
      frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [active]);

  const onEnded = useCallback((index) => {
    if (index !== active) return;
    if (Date.now() - lastClick.current < IDLE_AFTER_CLICK_MS) {
      playQuietly(videoRefs.current[index]);
      return;
    }
    setActive((index + 1) % DESK_FEATURES.length);
  }, [active]);

  const pick = (index) => {
    lastClick.current = Date.now();
    setActive(index);
  };

  const current = DESK_FEATURES[active];
  const tone = DESK_TONES[current.tone];

  return (
    <div ref={wrapRef} className="showcase-panel" style={{ '--dm-tone': tone.color, '--showcase-tint': tone.tint }}>
      <div className="showcase-tabs" role="tablist" aria-label="Features">
        {DESK_FEATURES.map((feature, index) => (
          <button
            key={feature.id}
            type="button"
            role="tab"
            aria-selected={index === active}
            className={index === active ? 'is-active' : ''}
            style={{ '--dm-tone': DESK_TONES[feature.tone].color }}
            onClick={() => pick(index)}
          >
            {feature.short}
            <span className="showcase-tabs__track">
              <span ref={(node) => { fillRefs.current[index] = node; }} className="showcase-tabs__fill" />
            </span>
          </button>
        ))}
      </div>

      <div className="showcase-stage">
        <div className="showcase-copy">
          {DESK_FEATURES.map((feature, index) => {
            const featureTone = DESK_TONES[feature.tone];
            return (
              <div key={feature.id} className={`showcase-copy__panel${index === active ? ' is-active' : ''}`} aria-hidden={index !== active} style={{ '--dm-tone': featureTone.color }}>
                <p className="dm-eyebrow">{feature.eyebrow}</p>
                <h3>{feature.title}</h3>
                <p className="dm-copy">{feature.copy}</p>
                {feature.stat ? <CountStat stat={feature.stat} tone={featureTone} run={seen.has(index)} /> : null}
                <Link to="/features" className="showcase-copy__link">
                  See how it works
                  <svg fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                  </svg>
                </Link>
              </div>
            );
          })}
        </div>

        <BrowserFrame className="showcase-frame">
          {DESK_FEATURES.map((feature, index) => (
            <div key={feature.id} className={`showcase-frame__layer${index === active ? ' is-active' : ''}`}>
              <Clip
                ref={(node) => { videoRefs.current[index] = node; }}
                clip={feature.clip}
                preload={index === 0 ? 'auto' : 'metadata'}
                onEnded={() => onEnded(index)}
              />
            </div>
          ))}
        </BrowserFrame>
      </div>
    </div>
  );
}

/* Desktop homepage features: tabbed product showcase, then the rating demo beside the P&L ledger. */
function DesktopFeatures() {
  return (
    <section className="showcase" aria-label="What HorseStat does">
      <div className="container">
        <header className="showcase-intro">
          <p className="dm-eyebrow">Everything in one place</p>
          <h2 className="dm-heading">See the whole race before you bet.</h2>
        </header>
        <Showcase />

        <div className="showcase-band">
          <div className="showcase-band__sim">
            <SimWidget />
          </div>
          <div className="showcase-band__pnl" style={{ '--dm-tone': DESK_TONES.teal.color }}>
            <p className="dm-eyebrow">Profit &amp; Loss Tracker</p>
            <h2>Know exactly where you stand.</h2>
            <p className="showcase-band__copy">
              Save selections into strategies, choose Win or Each Way at SP or your own odds, and every result settles into a running P&amp;L.
            </p>
            <PnlLedger dense />
            <p className="dm-note">{PNL_FOOTNOTE}</p>
          </div>
        </div>
      </div>
    </section>
  );
}

export default DesktopFeatures;
